import {
  CloseCircleFilled,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { partnerApi } from "api/partner.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Partner } from "types/partner";
import { $url } from "utils/url";
import { PartnerModal } from "./PartnerModal";

export const PartnerTab = () => {
  const [searchValue, setSearchValue] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Partner[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partial<Partner>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 })),
      300,
    ),
    [],
  );

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await partnerApi.findAll(query);
      setData(res.data.partners);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  const columns: TableColumnsType<Partner> = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      render: (_, __, index) => (
        <span>{(query.page - 1) * query.limit + index + 1}</span>
      ),
    },
    {
      title: "Tên",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <INA
          image={record.logo ? $url(record.logo) : undefined}
          name={record.name}
        />
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Cập nhật">
          <ActionButton
            aria-label="Cập nhật"
            onClick={() => {
              setSelectedPartner(record);
              setVisibleModal(true);
              setModalStatus("update");
            }}
          >
            <EditOutlined style={{ color: "#6c84e5" }} />
          </ActionButton>
        </Tooltip>
      ),
    },
  ];

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchValue,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearchValue(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearchValue("");
                        debounceSearch.cancel();
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedPartner({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Partner>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 500 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <PartnerModal
        visible={visibleModal}
        status={modalStatus}
        partner={selectedPartner}
        onClose={() => setVisibleModal(false)}
        onSubmitOk={fetchData}
      />
    </div>
  );
};
